import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Medal, TrendingUp, Star, Share2, Sparkles, Info, Shield, Swords, Crown, Trophy, Target, Briefcase, Trash2, Scale } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import LivingRankChart from "../components/recap/LivingRankChart";
import Takeover from "../components/onui/Takeover";
import { isPodiumMode } from "../lib/leaderboardRanking";

// Tailwind needs literal class names — podium column map (iter86.45.2).
const PODIUM_COLS = {
    1: "sm:grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3",
    4: "sm:grid-cols-4", 5: "sm:grid-cols-5", 6: "sm:grid-cols-6",
};
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";

/**
 * Public Tournament Recap page (/league/:id/recap?tournament_id=...).
 *
 * No auth required — copy-paste shareable in group chats. Shows champion,
 * final standings, awards, and a multi-line points-over-time chart powered
 * by Recharts. Renders live for ongoing tournaments too — the "champion" +
 * "best champion pick" sections gracefully hide until `status=completed`.
 *
 * iter63z'' — Now reads `?tournament_id=` from the URL. Without it the
 * server falls back to `league.active_tournament_id` which is the legacy
 * default and shows the WRONG tournament's recap when viewing a
 * non-default tournament.
 */
export default function LeagueRecap() {
    const { leagueId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const tournamentId = searchParams.get("tournament_id");
    const [recap, setRecap] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!leagueId) return;
        api.getRecap(leagueId, tournamentId)
            .then(r => setRecap(r))
            .catch(err => {
                toast.error(err.response?.data?.detail || "Could not load recap");
                navigate(`/league/${leagueId}`);
            })
            .finally(() => setLoading(false));
    }, [leagueId, tournamentId, navigate]);

    const handleShare = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({
                    title: `${recap.league.name} — Recap`,
                    text: `Check out the tournament recap for ${recap.league.name}`,
                    url,
                });
            } else {
                await navigator.clipboard.writeText(url);
                toast.success("Recap link copied to clipboard.");
            }
        } catch (e) {
            // share dialogue cancelled — silent
        }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading recap…</div>;
    if (!recap) return null;

    const { league, tournament, leaderboard, awards, stats, is_completed, trophy_race } = recap;
    // iter86.62 — Template-aware layout: knockout | season | golf_las | golf_draft | playoffs
    const recapMode = recap.mode || "knockout";
    const isGolf = recapMode === "golf_las" || recapMode === "golf_draft";
    const isLas = recapMode === "golf_las";
    // iter87.465.9/.11 — NFL/CFB/NWSL/MLB/NBA Playoffs ("playoffs") and
    // the 8 Season-Long templates ("season_v2") share the exact same
    // new Winners + Crewmate Awards shape; the generic knockout awards
    // (MVP Team, Biggest Mover, Shield Winner, KO Winner) never applied
    // to either.
    const isPlayoffs = recapMode === "playoffs" || recapMode === "season_v2";
    const showCrewAwards = recapMode === "knockout";
    const showMvp = !isGolf;
    const fmtScore = (p) => isLas
        ? (p.golf?.avg_score != null ? `${p.golf.avg_score} avg` : "—")
        : `${p.total_points} pts`;
    // Golf LAS: replay per-round avg instead of (empty) match series.
    const golfRideSeries = isLas && recap.round_series?.rows?.length > 1
        ? {
            keys: recap.round_series.keys,
            matches: (recap.round_series.rounds || []).map(r => ({
                index: r.index, label: r.label, score: "",
            })),
            rows: recap.round_series.rows.map(r => ({
                match_index: r.round_index, ranks: r.ranks, points: r.avg,
            })),
        }
        : null;
    // iter87.502 (user-reported) — Podium shape now follows the
    // tournament's own billing/Trophy Tokens config via `isPodiumMode`
    // (shared source of truth with LeaderboardTable.jsx), instead of
    // unconditionally rendering ranks 1-3: Trophy Tokens "Podium" gets
    // the tie-aware top-3; Free/Paid-no-tokens and Trophy Tokens
    // "Winner Take All" get a tie-aware single-winner spotlight (still
    // multiple tiles if rank 1 is tied, same convention as the podium).
    const podiumMode = isPodiumMode(tournament);
    const podium = podiumMode
        ? (leaderboard || []).filter((p, i) => (p.rank || i + 1) <= 3).slice(0, 6)
        : (leaderboard || []).filter((p, i) => (p.rank || i + 1) === 1).slice(0, 6);

    return (
        <Takeover
            open
            onClose={() => navigate(`/league/${leagueId}${tournamentId ? `?tournament_id=${tournamentId}` : ""}`)}
            title="Recap"
            testId="recap-takeover"
            maxWidthClass="lg:max-w-5xl"
            icon={Sparkles}
            iconTone="cyber"
            pill={
                <button
                    data-testid="recap-share-btn"
                    onClick={handleShare}
                    aria-label="Share recap"
                    className="w-9 h-9 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors active:scale-95 shrink-0 text-cyber"
                >
                    <Share2 className="w-4 h-4" />
                </button>
            }
        >
            <div className="max-w-4xl mx-auto space-y-6">
                <section className="text-center space-y-3">
                    <p className="text-xs uppercase tracking-widest text-zinc-500">{league.name}</p>
                    <h1 className="font-display font-extrabold text-3xl sm:text-4xl uppercase">
                        {tournament.name}
                    </h1>
                    <p className="text-zinc-400 text-sm">
                        {is_completed
                            ? "🏆 Tournament complete. Here's how it went down."
                            : "Recap in progress — final results pending tournament end."}
                    </p>
                </section>

                {/* iter84.17 — Champion *team* banner removed. The recap is a
                    player-centric leaderboard; the eventual tournament-winning
                    team is already surfaced via the "Most-Picked Champion" and
                    "Best Champion Pick" award cards below, which tie that team
                    back to the crewmates who called it. */}

                {/* Podium — tie-aware (iter86.45.2): backend ranks use
                    competition ("1224") ranking, so two players tied at the
                    top render 🥇🥇 + a 🥉 third-place tile. iter87.502
                    (user-reported) — shape now driven by `isPodiumMode`
                    (Trophy Tokens config): a 3-tier podium for Trophy
                    Tokens "Podium", a single-winner spotlight for every
                    other configuration (Free/Paid-no-tokens, Trophy
                    Tokens "Winner Take All"). */}
                {isPlayoffs && (podium.length === 0 || !podium[0].total_points) && (
                    <section data-testid="recap-winners-pending" className="brutal-card px-6 py-5 text-center">
                        <p className="text-2xl leading-none">🏆</p>
                        <p className="text-sm text-zinc-500 mt-1">To be decided</p>
                    </section>
                )}
                {podium.length > 0 && !(isPlayoffs && !podium[0].total_points) && (
                    <section data-testid="recap-top-three" className={`grid grid-cols-1 gap-3 ${PODIUM_COLS[Math.min(podium.length, 6)]}`}>
                        {podium.map((p, i) => {
                            const rank = p.rank || i + 1;
                            const medal = rank === 1 ? "🥇" : rank === 2 ? "🥈" : "🥉";
                            // iter87.319 (user-reported) — 1st place border was
                            // "border-blaze" (#FF6B1F, a bronze/copper-looking
                            // orange), not gold. Switched to yellow-500, matching
                            // the same gold/silver/bronze convention LeaderboardTable.jsx
                            // already uses for its 1st/2nd/3rd ribbons.
                            const accent = rank === 1 ? "border-yellow-500" : rank === 2 ? "border-zinc-400" : "border-amber-700";
                            return (
                                <div
                                    key={p.participant_id}
                                    className={`brutal-card px-4 py-3 text-center space-y-1 ${accent}`}
                                    data-testid={`recap-top-${i + 1}`}
                                    data-rank={rank}
                                >
                                    <p className="text-2xl leading-none">{medal}</p>
                                    <p className="font-bold text-sm truncate">{p.username}</p>
                                    <p className="text-base font-display font-extrabold text-cyber">
                                        {fmtScore(p)}
                                    </p>
                                </div>
                            );
                        })}
                    </section>
                )}

                {/* iter63z''' — Awards split into TEAM row + PLAYER row.
                    Team row (top): 2 cards (MVP Team, Most-Selected Champion Pick).
                    Player row: 6 cards (Best Champion Pick, Biggest Mover,
                    Burn Master Poster, Burn Master Reactor, Shield Winner, KO Winner).
                    iter87.465.9 — Retired entirely for Playoffs/Series mode;
                    replaced by PlayoffsAwards below. */}
                {!isPlayoffs && <section data-testid="recap-awards-team" className="space-y-2">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500">{isGolf ? "Awards" : "Team awards"}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {showMvp && <AwardCard
                            testid="recap-award-mvp"
                            icon={Star}
                            title="MVP Team"
                            subtitle={
                                awards.mvp_tied_teams?.length > 1
                                    ? `${awards.mvp_tied_teams.length}-way tie`
                                    : awards.mvp_team
                                        ? `${awards.mvp_team.flag || "⚽"} ${awards.mvp_team.name}`
                                        : "—"
                            }
                            meta={
                                awards.mvp_tied_teams?.length > 1
                                    ? awards.mvp_tied_teams
                                        .map(t => `${t.flag || "⚽"} ${t.name}`)
                                        .join(" · ")
                                    : awards.mvp_team
                                        ? `${awards.mvp_team.points_value} pts contributed`
                                        : "Not yet decided"
                            }
                            info={
                                <>
                                    <p className="font-bold text-sm mb-1">MVP Team</p>
                                    <p className="text-xs text-zinc-400 mb-2">
                                        Drafted team that contributed the most points to its owner(s).
                                        Calculated from group W/D points + per-round advance bonuses
                                        (using each tournament's live scoring rules).
                                    </p>
                                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                                        Tiebreakers
                                    </p>
                                    <ol className="text-xs text-zinc-400 list-decimal list-inside space-y-0.5">
                                        <li>Goal differential (GF − GA)</li>
                                        <li>Goals for (GF)</li>
                                        <li>Stage reached (later wins)</li>
                                    </ol>
                                    <p className="text-[10px] text-zinc-500 mt-2">
                                        If teams remain tied across all four, every tied team is listed.
                                    </p>
                                </>
                            }
                        />}
                        <AwardCard
                            testid="recap-award-most-picked"
                            icon={Crown}
                            title="Most-Picked Champion"
                            subtitle={
                                awards.most_picked_champion
                                    ? `${awards.most_picked_champion.flag || "⚽"} ${awards.most_picked_champion.name}`
                                    : "—"
                            }
                            meta={
                                awards.most_picked_champion
                                    ? `${awards.most_picked_champion.pick_count} of ${awards.most_picked_champion.total_pickers} pick${awards.most_picked_champion.total_pickers === 1 ? "" : "s"}${awards.most_picked_champion.tied_count > 1 ? ` · ${awards.most_picked_champion.tied_count}-way tie` : ""}`
                                    : "No champion picks yet"
                            }
                            info={
                                <>
                                    <p className="font-bold text-sm mb-1">Most-Picked Champion</p>
                                    <p className="text-xs text-zinc-400 mb-2">
                                        The team that the most players chose as their bonus
                                        champion pick before knockouts began — i.e., the
                                        consensus pre-knockout favorite.
                                    </p>
                                    <p className="text-[10px] text-zinc-500">
                                        Ties on count are broken alphabetically; the total
                                        number of pickers in the rivalry is shown for context.
                                    </p>
                                </>
                            }
                        />
                    </div>
                </section>}

                {/* iter87.465.9 — New template-aware Crewmate Awards for
                    Playoffs/Series mode (replaces the retired knockout set
                    above for this mode only). */}
                {isPlayoffs && <PlayoffsAwards awards={recap.awards} />}

                {showCrewAwards && <section data-testid="recap-awards-player" className="space-y-2">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500">Crewmate awards</p>
                    {/* iter63z''''''' — Resized from `xl:grid-cols-6` (6 slots) to a
                        4-col layout after Burn Poster + Burn Reactor were removed from
                        per-tournament recap (iter63z'''''). Now 4 cards stretch evenly. */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <AwardCard
                            testid="recap-award-champion-pick"
                            icon={Medal}
                            title="Best Champion Pick"
                            subtitle={
                                awards.best_champion_pickers?.length
                                    ? awards.best_champion_pickers.map(p => p.username).join(", ")
                                    : "—"
                            }
                            meta={
                                awards.best_champion_pickers?.length
                                    ? `${awards.best_champion_pickers.length} player${awards.best_champion_pickers.length === 1 ? "" : "s"} called the eventual champion`
                                    : (is_completed ? "No one picked the champion" : "Pending tournament end")
                            }
                            info={
                                <>
                                    <p className="font-bold text-sm mb-1">Best Champion Pick</p>
                                    <p className="text-xs text-zinc-400 mb-2">
                                        Every crewmate whose <span className="text-cyber">bonus
                                        champion pick</span> ended up actually winning the tournament.
                                    </p>
                                    <p className="text-xs text-zinc-400 mb-2">
                                        Players lock in their bonus pick before the knockout rounds
                                        begin — so this card celebrates the people who called the
                                        winner correctly while the field was still wide open.
                                    </p>
                                    <p className="text-[10px] text-zinc-500">
                                        If multiple players picked the same eventual champion, they all
                                        get the badge. Until a champion is crowned, this stays pending.
                                    </p>
                                </>
                            }
                        />
                        <AwardCard
                            testid="recap-award-biggest-mover"
                            icon={TrendingUp}
                            title="Biggest Mover"
                            subtitle={awards.biggest_mover?.username || "—"}
                            meta={
                                awards.biggest_mover
                                    ? `Climbed ${awards.biggest_mover.positions_climbed} spot${awards.biggest_mover.positions_climbed === 1 ? "" : "s"} (#${awards.biggest_mover.from_rank} → #${awards.biggest_mover.to_rank})`
                                    : (stats.snapshots_count
                                        ? "No one has changed ranks yet"
                                        : "Tracking starts after the first snapshot")
                            }
                            info={
                                <>
                                    <p className="font-bold text-sm mb-1">Biggest Mover</p>
                                    <p className="text-xs text-zinc-400 mb-2">
                                        Player who climbed the most positions on the leaderboard since
                                        the tournament's <span className="text-cyber">first snapshot</span>.
                                    </p>
                                    <p className="text-xs text-zinc-400 mb-2">
                                        Snapshots are taken automatically when the tournament starts,
                                        when each knockout round begins, and hourly when ranks change.
                                    </p>
                                    <p className="text-[10px] text-zinc-500">
                                        Ties at zero positions = no mover yet. Ranks compared from the
                                        earliest snapshot to the current live leaderboard.
                                    </p>
                                </>
                            }
                        />
                        {/* iter63z''''' — Burn Poster / Burn Reactor pills REMOVED from the
                            per-tournament recap. Smack Stream is now league-level, so these
                            awards are computed once at the season level and banked into
                            Rivalry Standings instead. See `crown_points.py` season block. */}
                        <AwardCard
                            testid="recap-award-shield"
                            icon={Shield}
                            title="Shield Winner"
                            subtitle={awards.shield_winner?.username || "—"}
                            meta={
                                awards.shield_winner
                                    ? `${awards.shield_winner.days_at_top} day${awards.shield_winner.days_at_top === 1 ? "" : "s"} at #1 in group stage${awards.shield_winner.tied_count > 1 ? ` · ${awards.shield_winner.tied_count}-way tie` : ""}`
                                    : "No group-stage snapshots yet"
                            }
                            info={
                                <>
                                    <p className="font-bold text-sm mb-1">Shield Winner</p>
                                    <p className="text-xs text-zinc-400 mb-2">
                                        Player who held the <span className="text-cyber">#1
                                        spot on the leaderboard</span> for the most distinct days
                                        during the group stage of this tournament.
                                    </p>
                                    <p className="text-[10px] text-zinc-500">
                                        Counted from leaderboard snapshots taken before the
                                        first knockout round started. Players tied at #1 on a
                                        given day each get credit for that day.
                                    </p>
                                </>
                            }
                        />
                        <AwardCard
                            testid="recap-award-ko"
                            icon={Swords}
                            title="KO Winner"
                            subtitle={awards.ko_winner?.username || "—"}
                            meta={
                                awards.ko_winner
                                    ? `${awards.ko_winner.ko_points} KO points${awards.ko_winner.tied_count > 1 ? ` · ${awards.ko_winner.tied_count}-way tie` : ""}`
                                    : "No knockout points yet"
                            }
                            info={
                                <>
                                    <p className="font-bold text-sm mb-1">KO Winner</p>
                                    <p className="text-xs text-zinc-400 mb-2">
                                        Player whose drafted teams earned the most
                                        <span className="text-cyber"> knockout-stage advancement
                                        points</span> (the per-round bonuses for reaching R32,
                                        R16, QF, SF, Final, and Champion).
                                    </p>
                                    <p className="text-[10px] text-zinc-500">
                                        Group-stage W/D points and bonus champion-pick points
                                        are excluded — this is a pure knockout-run metric.
                                    </p>
                                </>
                            }
                        />
                    </div>
                </section>}

                {/* iter47g — Animated bump chart (livingcharts.com style).
                    Plays back rank/points changes match-by-match with smooth
                    transitions + scrubable timeline.
                    iter86.62 — Golf LAS replays per-round avg via round_series. */}
                {(golfRideSeries || recap?.match_series?.rows?.length > 1) && (
                    <section data-testid="recap-chart" className="brutal-card p-5 space-y-3">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-cyber" />
                            <h2 className="font-display font-extrabold text-xl uppercase">
                                The Ride
                            </h2>
                        </div>
                        <p className="text-xs text-zinc-500">
                            {golfRideSeries
                                ? "Hit play to watch ranks shift after every round. Drag the slider to scrub through history."
                                : "Hit play to watch ranks shift after every match. Drag the slider to scrub through history."}
                        </p>
                        <LivingRankChart
                            matchSeries={golfRideSeries || recap.match_series}
                            metric={golfRideSeries ? "avg" : "points"}
                            stepPrefix={golfRideSeries ? "R" : "M"}
                            fallbackValue={golfRideSeries ? recap.round_series?.course_par : null}
                        />
                    </section>
                )}

                {/* iter86.47 → iter87.108 — AI Ride Story (Pro+ / Comp perk).
                    iter87.199.94 — Display gate collapsed to `is_completed`
                    (i.e. `tournament.status == "completed"`), matching the
                    single fire point in `apply_tournament_completion`. Show
                    ONLY when the tournament is marked complete AND a story
                    exists; hide otherwise regardless of `ride_story` field.
                    Removes template-specific "final vs completed" drift
                    (World Cup API, Custom API, EPL) in one gate. */}
                {recap.ride_story?.text && recap.is_completed && (
                    <section className="brutal-card p-5 space-y-2 border-cyber" data-testid="recap-ride-story">
                        <div className="flex items-center gap-2">
                            <h2 className="font-display font-extrabold text-lg uppercase tracking-tight">
                                📖 The Ride — As Told By Our Storyteller
                            </h2>
                            <span className="pill bg-cyber/15 border-cyber text-cyber text-[9px] font-bold">Pro+</span>
                        </div>
                        {recap.ride_story.text.split("\n").filter(Boolean).map((para, i) => (
                            <p key={i} className="text-sm text-zinc-300 leading-relaxed">{para}</p>
                        ))}
                        <p className="text-[10px] text-zinc-600">
                            Written by AI from your crew's actual ride chart. All in good fun.
                        </p>
                    </section>
                )}

                {/* Full standings + Trophy Race (side-by-side from iPad width up) */}
                <div className={`grid grid-cols-1 gap-4 ${trophy_race ? "md:grid-cols-2" : ""}`}>
                    <section data-testid="recap-full-leaderboard" className="brutal-card p-5 border-cyber">
                        <div className="flex items-center gap-2 mb-1">
                            <Crown className="w-5 h-5 text-cyber" />
                            <h2 className="font-display font-extrabold text-xl uppercase">
                                Field Standings
                            </h2>
                        </div>
                        <p className="text-xs text-zinc-500 mb-3">
                            (the thing that truly matters... well, only if you're winning or something like that)
                        </p>
                        <div className="space-y-1">
                            {leaderboard?.map((p, i) => (
                                <div
                                    key={p.participant_id}
                                    className="flex items-center justify-between gap-2 py-2 border-b border-zinc-800 last:border-0"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className="text-zinc-500 font-mono text-xs w-6 text-right">#{p.rank ?? i + 1}</span>
                                        <span className="font-bold truncate">{p.username}</span>
                                    </div>
                                    <span className="text-cyber font-display font-extrabold">{fmtScore(p)}</span>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* iter87.199.194 — Trophy Race: confirmed-only Trophy Tokens
                        crewmates, ranked by a fun (not book-grade) "Win Chance %"
                        that blends point lead with how much of the tournament is
                        still left to play. Hidden entirely when Trophy Tokens
                        isn't enabled or fewer than 2 crewmates have confirmed. */}
                    {trophy_race && (
                        <section data-testid="recap-trophy-race" className="brutal-card p-5 border-volt/40">
                            <div className="flex items-center gap-2 mb-1">
                                <Trophy className="w-5 h-5 text-volt" />
                                <h2 className="font-display font-extrabold text-xl uppercase">
                                    Trophy Race
                                </h2>
                            </div>
                            <p className="text-xs text-zinc-500 mb-3">
                                Win Chance among confirmed Trophy Tokens crewmates — just for fun, not a real forecast.
                            </p>
                            <div className="space-y-1">
                                {trophy_race.rows.map((p, i) => (
                                    <div
                                        key={p.participant_id}
                                        data-testid={`trophy-race-row-${p.participant_id}`}
                                        className="flex items-center justify-between gap-2 py-2 border-b border-zinc-800 last:border-0"
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <span className="text-zinc-500 font-mono text-xs w-6 text-right">#{p.rank ?? i + 1}</span>
                                            <span className="font-bold truncate">{p.username}</span>
                                            <span className="text-[10px] text-zinc-500 shrink-0">{fmtScore(p)}</span>
                                        </div>
                                        <span
                                            data-testid={`trophy-race-pct-${p.participant_id}`}
                                            className="text-volt font-display font-extrabold"
                                        >
                                            {p.win_chance_pct}%
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>

                <footer className="text-center text-[10px] text-zinc-600 pb-4">
                    {isPlayoffs
                        ? "Recap generated live · Tiny Crew Rivalries"
                        : <>Recap generated live from {stats.snapshots_count} snapshot{stats.snapshots_count === 1 ? "" : "s"} · Tiny Crew Rivalries</>}
                </footer>
            </div>
        </Takeover>
    );
}

function AwardCard({ icon: Icon, title, subtitle, meta, info, testid, wrapTitle }) {
    return (
        <div className="brutal-card-light p-4 space-y-1" data-testid={testid}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <Icon className="w-4 h-4 text-cyber shrink-0" />
                    <p className={`text-[10px] uppercase tracking-widest text-zinc-500 leading-tight ${wrapTitle ? "" : "truncate"}`}>{title}</p>
                </div>
                {info && (
                    <Popover>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                aria-label={`${title} explainer`}
                                data-testid={testid ? `${testid}-info-btn` : "award-info-btn"}
                                className="w-5 h-5 rounded-full border border-zinc-700 text-zinc-500 hover:text-cyber hover:border-cyber transition-colors flex items-center justify-center"
                            >
                                <Info className="w-3 h-3" />
                            </button>
                        </PopoverTrigger>
                        <PopoverContent
                            align="end"
                            sideOffset={6}
                            data-testid={testid ? `${testid}-info-popover` : "award-info-popover"}
                            className="w-72 bg-ink border border-cyber/40 text-white"
                        >
                            {info}
                        </PopoverContent>
                    </Popover>
                )}
            </div>
            <p className="font-bold text-sm truncate">{subtitle}</p>
            <p className="text-[10px] text-zinc-500 truncate" title={meta}>{meta}</p>
        </div>
    );
}

// iter87.465.9 — Playoffs/Series Crewmate Awards. Every card is
// template-aware on the backend already (e.g. `most_picked_darkhorse`
// comes back null for MLB/NBA, which have no Dark Horse pick) — this
// component just hides whichever cards come back null instead of
// rendering an empty/broken tile.
function PlayoffsAwards({ awards }) {
    if (!awards) return null;
    const cards = [
        awards.most_picked_champion && {
            testid: "recap-award-most-picked-champion", icon: Crown, title: "Most-Picked Champion",
            subtitle: `${awards.most_picked_champion.flag || "🏆"} ${awards.most_picked_champion.name || "Unknown team"}`,
            meta: `${awards.most_picked_champion.pick_count} of ${awards.most_picked_champion.total_pickers} crewmate${awards.most_picked_champion.total_pickers === 1 ? "" : "s"}${awards.most_picked_champion.tied_count > 1 ? ` · ${awards.most_picked_champion.tied_count}-way tie` : ""}`,
            info: <p className="text-xs text-zinc-400">The team the most crewmates chose to win it all.</p>,
        },
        awards.most_picked_darkhorse && {
            testid: "recap-award-most-picked-darkhorse", icon: Swords, title: "Most-Picked Dark Horse",
            subtitle: `${awards.most_picked_darkhorse.flag || "🐴"} ${awards.most_picked_darkhorse.name || "Unknown team"}`,
            meta: `${awards.most_picked_darkhorse.pick_count} of ${awards.most_picked_darkhorse.total_pickers} crewmate${awards.most_picked_darkhorse.total_pickers === 1 ? "" : "s"}${awards.most_picked_darkhorse.tied_count > 1 ? ` · ${awards.most_picked_darkhorse.tied_count}-way tie` : ""}`,
            info: <p className="text-xs text-zinc-400">The underdog team the most crewmates bet on as their Dark Horse.</p>,
        },
        // iter87.465.16 (user-requested) — Future GM now ordered ABOVE
        // the Human Prediction Market Award.
        awards.future_gm && {
            testid: "recap-award-future-gm", icon: Briefcase, title: "Future GM Award",
            subtitle: awards.future_gm.username,
            meta: `${awards.future_gm.points} pts so far${awards.future_gm.tied_count > 1 ? ` · ${awards.future_gm.tied_count}-way tie` : ""}`,
            info: <p className="text-xs text-zinc-400">Highest point total from pre-season picks (draft/pot/champion/power pick) — proof they could run a front office.</p>,
        },
        awards.human_prediction_market && {
            testid: "recap-award-predictor", icon: Target, title: "The Human Prediction Market Award", wrapTitle: true,
            subtitle: awards.human_prediction_market.username,
            meta: `${awards.human_prediction_market.points} pts${awards.human_prediction_market.tied_count > 1 ? ` · ${awards.human_prediction_market.tied_count}-way tie` : ""}`,
            info: <p className="text-xs text-zinc-400">Highest game/week prediction point total so far — the crewmate calling the action best.</p>,
        },
        awards.relegated && {
            testid: "recap-award-relegated", icon: Trash2, title: "The Wooden Spoon",
            subtitle: awards.relegated.username,
            meta: `${awards.relegated.total_points} pts${awards.relegated.tied_count > 1 ? ` · ${awards.relegated.tied_count}-way tie` : ""} · currently last`,
            info: <p className="text-xs text-zinc-400">Currently last on the leaderboard — still time to turn it around.</p>,
        },
        awards.goldilocks && {
            testid: "recap-award-goldilocks", icon: Scale, title: "The Goldilocks Award", wrapTitle: true,
            subtitle: awards.goldilocks.username,
            meta: `${awards.goldilocks.total_points} pts · field avg ${awards.goldilocks.average}`,
            info: <p className="text-xs text-zinc-400">Not too hot, not too cold — the score closest to the field average.</p>,
        },
    ].filter(Boolean);
    if (!cards.length) return null;
    return (
        <section data-testid="recap-awards-playoffs" className="space-y-2">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Crewmate awards</p>
            {/* iter87.465.16 (user-reported) — one continuous grid instead
                of two separate team/player grids: when a card is hidden
                (e.g. Most-Picked Dark Horse on season-long templates),
                the next card now flows up into that spot instead of
                leaving a mismatched empty cell that read as a stray gap. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {cards.map(c => <AwardCard key={c.testid} {...c} />)}
            </div>
        </section>
    );
}
